import type { Observable } from 'rxjs';

import type {
  DestinationStaticInfo,
  PortStaticInfo,
  CarrierName,
  CarrierStaticInfo,
  OverviewStaticInfoResult,
  DetailsStaticInfo,
  HotelConstraintQL,
  HotelIngredientAdapter,
  TransportVehicle,
  FerryTicketCategory,
  Coordinate,
  IngredientId,
  ActivityStaticInfo,
  NeighborhoodPolygonsOnlyHotel,
} from '@libs/trip-planner-interfaces';

import { OverviewStaticInfoSearchService } from './services/overviewStaticInfoSearchService';
import { DestinationStaticInfoSearchService } from './services/destinationStaticInfoSearchService';
import { DetailsStaticInfoSearchService } from './services/detailsStaticInfoSearchService';
import { TransportStaticInfoSearchSercice } from './services/transportStaticInfoSearchService';

export class StaticInfoSearchService {
  private overviewSearchService = new OverviewStaticInfoSearchService();
  private destinationSearchService = new DestinationStaticInfoSearchService();
  private detailsSearchService = new DetailsStaticInfoSearchService();
  private transportSearchService = new TransportStaticInfoSearchSercice();

  public searchOverview(
    ingredients: HotelIngredientAdapter[]
  ): Observable<OverviewStaticInfoResult> {
    return this.overviewSearchService.search(ingredients);
  }

  public searchDestination(
    ingredient: HotelIngredientAdapter
  ): Observable<Record<string, DestinationStaticInfo>> {
    return this.destinationSearchService.search(ingredient);
  }

  public searchMinimumActivityStaticInfo(
    constraint: HotelConstraintQL
  ): Observable<boolean> {
    return this.overviewSearchService.searchMinimumActivityStaticInfo(
      constraint
    );
  }

  public getActivitiesStaticInfo(
    codes: string[]
  ): Observable<ActivityStaticInfo[]> {
    return this.overviewSearchService.getActivitiesStaticInfo(codes);
  }

  public searchDestinationSuggestedActivities(
    constraint: HotelConstraintQL
  ): Observable<ActivityStaticInfo[]> {
    return this.overviewSearchService.searchSuggestedActivitiesStaticInfo(
      constraint
    );
  }

  public searchDetails(
    hotelCode: string,
    ingredientId: IngredientId
  ): Observable<DetailsStaticInfo> {
    return this.detailsSearchService.search(hotelCode, ingredientId);
  }

  public searchHotelName(inputText: string): Observable<string> {
    return this.detailsSearchService.searchHotelName(inputText);
  }

  public searchHotelCode(hotelName: string): Observable<string | undefined> {
    return this.detailsSearchService.searchHotelCode(hotelName);
  }

  public searchHotelMainPolygon(
    inputText: string
  ): Observable<{ name?: string; id?: number }> {
    return this.detailsSearchService.searchHotelMainPolygon(inputText);
  }

  public searchHotelPosition(
    inputText: string
  ): Observable<Coordinate | undefined> {
    return this.detailsSearchService.searchHotelPosition(inputText);
  }

  public searchHotelNeighborhoodPolygons(
    inputText: string
  ): Observable<NeighborhoodPolygonsOnlyHotel[] | undefined> {
    return this.detailsSearchService.searchHotelNeighborhoodPolygons(inputText);
  }

  public searchCarrierStaticInfo(
    carrierCode: string
  ): Observable<CarrierStaticInfo> {
    return this.transportSearchService.searchCarrierStaticInfo(carrierCode);
  }

  public searchCarrierNames(
    carrierCodes: string[]
  ): Observable<Record<string, CarrierName>> {
    return this.transportSearchService.searchCarrierNames(carrierCodes);
  }

  public searchAirportStaticInfo(iata: string): Observable<PortStaticInfo> {
    return this.transportSearchService.searchAirportStaticInfo(iata);
  }

  public searchSeaportStaticInfo(iata: string): Observable<PortStaticInfo> {
    return this.transportSearchService.searchSeaportStaticInfo(iata);
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    return this.transportSearchService.getFerryVehicleTypes();
  }

  public getFerryTicketCategories(
    key: string
  ): Observable<FerryTicketCategory[]> {
    return this.transportSearchService.getFerryTicketCategories(key);
  }
}
